import React, { useState } from 'react';
import styles from './OrderLookupPage.module.scss';
import orderService from '../../services/orderService';
import { toast } from 'react-toastify';
import { 
  IoSearch, IoArrowBack, IoRefreshOutline, IoEyeOutline, 
  IoTimeOutline, IoCheckmarkCircle, IoCloseCircle 
} from 'react-icons/io5';

// 1. IMPORT HOOK LOADING
import { useLoading } from '../../context/LoadingContext/LoadingContext';

const BACKEND_URL = 'https://huongtramcomoc.vn';

// Map trạng thái
const STATUS_MAP = {
  'Pending':   { label: 'Chờ xác nhận', class: 'pending' },
  'Confirmed': { label: 'Đã xác nhận',  class: 'confirmed' },
  'Shipped':   { label: 'Đang giao hàng', class: 'shipped' },
  'Completed': { label: 'Giao thành công', class: 'completed' },
  'Cancelled': { label: 'Đã hủy',       class: 'cancelled' },
};

// Helper format ngày giờ
const formatDateTime = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return {
    time: date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }),
    date: date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })
  };
};

// --- COMPONENT: TIMELINE TRẠNG THÁI ---
const StatusTimeline = ({ order }) => {
  if (order.status === 'Cancelled') {
    const cancelTime = formatDateTime(order.status_cancelled_at || order.updated_at);
    return (
      <div className={styles.timelineContainer}>
        <div className={`${styles.timelineItem} ${styles.cancelledItem}`}>
          <div className={styles.timelineIcon}><IoCloseCircle /></div>
          <div className={styles.timelineContent}>
            <h4>Đơn hàng đã bị hủy</h4>
            {cancelTime && <p><IoTimeOutline /> {cancelTime.time} - {cancelTime.date}</p>}
            {order.notes && <p className={styles.cancelReason}>Ghi chú: {order.notes}</p>}
          </div>
        </div>
      </div>
    );
  }

  const steps = [
    { key: 'created', name: 'Đặt hàng thành công', dateKey: 'created_at' },
    { key: 'Confirmed', name: 'Đã xác nhận đơn hàng', dateKey: 'status_confirmed_at' },
    { key: 'Shipped', name: 'Đã giao cho đơn vị vận chuyển', dateKey: 'status_shipped_at' },
    { key: 'Completed', name: 'Giao hàng thành công', dateKey: 'status_completed_at' },
  ];

  let currentStatusIndex = -1;
  if (order.status === 'Pending') currentStatusIndex = 0;
  else if (order.status === 'Confirmed') currentStatusIndex = 1;
  else if (order.status === 'Shipped') currentStatusIndex = 2;
  else if (order.status === 'Completed') currentStatusIndex = 3;

  return (
    <div className={styles.timelineContainer}>
      {steps.map((step, index) => {
        const isActive = index <= currentStatusIndex && (index === 0 || order[step.dateKey]);
        const dateTime = formatDateTime(order[step.dateKey]);

        return (
          <div key={index} className={`${styles.timelineItem} ${isActive ? styles.activeItem : ''}`}>
            {index < steps.length - 1 && <div className={styles.timelineLine}></div>}
            <div className={styles.timelineIcon}>
              {isActive ? <IoCheckmarkCircle /> : <div className={styles.emptyCircle}></div>}
            </div>
            <div className={styles.timelineContent}>
              <h4>{step.name}</h4>
              {dateTime && isActive && (
                <p><IoTimeOutline /> {dateTime.time} ngày {dateTime.date}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- COMPONENT: CHI TIẾT ĐƠN HÀNG ---
const OrderDetailView = ({ order, onBack, onRefresh }) => {
  const formatPrice = (price) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const visibleProducts = showAllProducts ? order.items : order.items.slice(0, 3);
  const hiddenProductCount = order.items.length - visibleProducts.length;

  // Lấy hook loading để dùng cho nút Hủy
  const { showLoading, hideLoading } = useLoading();

  const handleCancelOrder = () => {
    if (window.confirm("Bạn có chắc chắn muốn hủy đơn hàng này không?")) {
      showLoading(); // Bật loading
      orderService.cancelOrder(order.id, order.customer_phone)
        .then(() => {
          toast.success("Đã hủy đơn hàng thành công.");
          onRefresh(); 
        })
        .catch(err => toast.error(err.response?.data?.message || "Lỗi hủy đơn"))
        .finally(() => {
          hideLoading(); // Tắt loading
        });
    }
  };
  
  const canCancel = order.status === 'Pending';

  return (
    <div className={styles.detailViewWrapper}>
      <div className={styles.detailHeaderNav}>
        <button onClick={onBack} className={styles.backButton}><IoArrowBack /> Quay lại</button>
        <h2 className={styles.mobileTitle}>Đơn hàng #{order.id}</h2>
        <div style={{width: '70px'}}></div>
      </div>

      <div className={styles.layoutGrid}>
        <div className={styles.leftColumn}>
           <div className={styles.card}>
            <h3 className={styles.cardTitle}>Trạng thái đơn hàng</h3>
            <div className={styles.stepperSection}>
              <StatusTimeline order={order} />
            </div>
            {canCancel && (
              <div className={styles.cancelSection}>
                 <button onClick={handleCancelOrder} className={styles.cancelButton}>Yêu cầu hủy đơn hàng</button>
                 <p className={styles.cancelNote}>*Chỉ có thể hủy khi đơn hàng chưa được xác nhận.</p>
              </div>
            )}
          </div>

          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Thông tin nhận hàng</h3>
            <div className={styles.infoGroup}>
              <label>Người nhận:</label>
              <span>{order.customer_name}</span>
            </div>
            <div className={styles.infoGroup}>
              <label>Số điện thoại:</label>
              <span>{order.customer_phone}</span>
            </div>
            <div className={styles.infoGroup}>
              <label>Địa chỉ:</label>
              <span>{order.customer_address}</span>
            </div>
            {order.notes && (
              <div className={styles.infoGroup}>
                <label>Ghi chú:</label>
                <span className={styles.noteText}>{order.notes}</span>
              </div>
            )}
          </div>
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Danh sách sản phẩm ({order.items.length})</h3>
            <div className={styles.productList}>
               {visibleProducts.map((item, index) => (
                 <div key={index} className={styles.item}>
                   <div className={styles.itemImageWrapper}>
                     <img src={item.thumbnail_url ? `${BACKEND_URL}${item.thumbnail_url}` : '/images/placeholder.jpg'} alt={item.name} className={styles.itemImage} />
                     <span className={styles.itemQuantityBadge}>x{item.quantity}</span>
                   </div>
                   <div className={styles.itemInfo}>
                      <span className={styles.itemName}>{item.name}</span>
                      <span className={styles.itemSinglePrice}>{formatPrice(item.price_at_purchase)}</span>
                   </div>
                   <span className={styles.itemTotal}>{formatPrice(item.price_at_purchase * item.quantity)}</span>
                 </div>
               ))}
               {hiddenProductCount > 0 && (
                 <button onClick={() => setShowAllProducts(!showAllProducts)} className={styles.showMoreButton}>
                   {showAllProducts ? 'Thu gọn' : `Xem thêm ${hiddenProductCount} sản phẩm khác ↓`}
                 </button>
               )}
            </div>
            
            <div className={styles.summarySection}>
               <div className={styles.summaryRow}><span>Tạm tính:</span><span>{formatPrice(order.total_amount)}</span></div>
               <div className={styles.summaryRow}><span>Phí vận chuyển:</span><span>Miễn phí</span></div>
               <div className={`${styles.summaryRow} ${styles.grandTotalRow}`}>
                  <span>Tổng thanh toán:</span>
                  <span className={styles.grandTotalValue}>{formatPrice(order.total_amount)}</span>
               </div>
               
               <div className={styles.paymentMethodBox}>
                  <span className={styles.paymentLabel}>Phương thức thanh toán:</span>
                  <span className={styles.paymentValue}>
                    {order.payment_method === 'cod' ? 'Thanh toán khi nhận hàng (COD)' : 'Chuyển khoản ngân hàng'}
                  </span>
               </div>
               
               {order.payment_proof_url && (
                 <div className={styles.proofContainer}>
                   <strong>Minh chứng chuyển khoản:</strong>
                   <a href={`${BACKEND_URL}${order.payment_proof_url}`} target="_blank" rel="noreferrer">
                      <img src={`${BACKEND_URL}${order.payment_proof_url}`} alt="Minh chứng" />
                   </a>
                 </div>
               )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- COMPONENT CHÍNH (VIEW TỔNG) ---
const OrderLookupPage = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [ordersList, setOrdersList] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Bỏ state loading cục bộ, dùng Hook
  const { showLoading, hideLoading } = useLoading();

  const formatPrice = (price) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);
  const formatDate = (dateString) => new Date(dateString).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const handleSearch = (e) => {
    e.preventDefault();
    if (!phone.trim()) { toast.warning("Vui lòng nhập số điện thoại"); return; }
    
    showLoading(); // Bật loading
    setOrdersList(null);
    setSelectedOrder(null);
    
    orderService.lookupOrder(name, phone)
      .then(res => { 
        setOrdersList(res.data); 
        if(res.data.length === 0) toast.info("Không tìm thấy đơn hàng nào với SĐT này.");
      })
      .catch(err => { 
        toast.error(err.response?.data?.message || "Lỗi khi tra cứu."); 
      })
      .finally(() => {
        hideLoading(); // Tắt loading
      });
  };

  const handleViewDetails = (orderId) => {
    showLoading(); // Bật loading
    orderService.getPublicOrderDetails(orderId, phone)
      .then(res => { 
        setSelectedOrder(res.data); 
        window.scrollTo(0,0); 
      })
      .catch(err => { 
        toast.error("Lỗi tải chi tiết đơn hàng."); 
      })
      .finally(() => {
        hideLoading(); // Tắt loading
      });
  };
  
  const refreshOrderDetails = (orderId) => {
     if(ordersList) {
         setOrdersList(prev => prev.map(o => o.id === orderId ? {...o, status: 'Cancelled'} : o));
     }
     handleViewDetails(orderId);
  };

  // VIEW 1: CHI TIẾT ĐƠN HÀNG
  if (selectedOrder) {
    return (
      <div className={styles.pageContainerDetail}>
        <OrderDetailView 
          order={selectedOrder} 
          onBack={() => setSelectedOrder(null)} 
          onRefresh={() => refreshOrderDetails(selectedOrder.id)}
        />
      </div>
    );
  }

  // VIEW 2: DANH SÁCH KẾT QUẢ
  if (ordersList) {
    return (
      <div className={styles.pageContainer}>
        <h1 className={styles.pageTitle}>Đơn Hàng Của Bạn</h1>
        <p className={styles.resultCount}>
          Tìm thấy <strong>{ordersList.length}</strong> đơn hàng gắn với SĐT <strong>{phone}</strong>
        </p>
        
        <div className={styles.orderListWrapper}>
          {ordersList.map(order => {
             const statusLabel = STATUS_MAP[order.status]?.label || order.status;
             const statusClass = STATUS_MAP[order.status]?.class || 'pending';
             return (
                <div key={order.id} className={styles.orderCardItem} onClick={() => handleViewDetails(order.id)}>
                   <div className={styles.ociHeader}>
                      <span className={styles.ociCode}>Đơn hàng #{order.id}</span>
                      <span className={`${styles.statusTag} ${styles[statusClass]}`}>{statusLabel}</span>
                   </div>
                   <div className={styles.ociBody}>
                      <div className={styles.ociRow}>
                         <span className={styles.ociLabel}>Ngày đặt:</span>
                         <span className={styles.ociValue}>{formatDate(order.created_at)}</span>
                      </div>
                      <div className={styles.ociRow}>
                         <span className={styles.ociLabel}>Tổng tiền:</span>
                         <span className={styles.ociPrice}>{formatPrice(order.total_amount)}</span>
                      </div>
                   </div>
                   <div className={styles.ociFooter}>
                      <span className={styles.viewDetailLink}>Xem chi tiết <IoEyeOutline /></span>
                   </div>
                </div>
             )
          })}
        </div>
        
        <div className={styles.footerActions}>
           <button onClick={() => setOrdersList(null)} className={styles.reSearchBtn}>
             <IoRefreshOutline /> Tra cứu số điện thoại khác
           </button>
        </div>
      </div>
    );
  }

  // VIEW 3: FORM TRA CỨU
  return (
    <div className={styles.splitLayoutWrapper}>
       <div className={styles.splitCard}>
          {/* CỘT TRÁI */}
          <div className={styles.illustrationSide}>
             <img 
               src="/shipper.gif" 
               alt="Delivery Tracking" 
               className={styles.trackingImage}
             />
             <div className={styles.illustrationText}>
                <h3>Theo Dõi Hành Trình</h3>
                <p>Cập nhật trạng thái đơn hàng ngay lập tức.</p>
             </div>
          </div>

          {/* CỘT PHẢI */}
          <div className={styles.formSide}>
             <div className={styles.formContent}>
                <h1 className={styles.splitTitle}>Tra Cứu Đơn Hàng</h1>
                <p className={styles.splitSubtitle}>Nhập số điện thoại để kiểm tra.</p>
                
                <form onSubmit={handleSearch}>
                  <div className={styles.formGroup}>
                    <label>Họ và Tên (Không bắt buộc)</label>
                    <input 
                      type="text" 
                      value={name} 
                      onChange={(e) => setName(e.target.value)} 
                      placeholder="Ví dụ: Nguyễn Văn A" 
                      className={styles.inputField}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Số Điện Thoại <span style={{color:'red'}}>*</span></label>
                    <input 
                      type="tel" 
                      value={phone} 
                      onChange={(e) => setPhone(e.target.value)} 
                      placeholder="Ví dụ: 0912345678" 
                      required 
                      className={styles.inputField}
                    />
                  </div>
                  <button type="submit" className={styles.submitButton}>
                    <IoSearch /> Tra Cứu Ngay
                  </button>
                </form>
             </div>
          </div>
       </div>
    </div>
  );
};

export default OrderLookupPage;